import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import publicUrls from "../routes/publicUrls";
import { Helmet } from "react-helmet";

const Faq = () => {
  localStorage.removeItem("@storeAuctionLot");

  const isMobile = useMediaQuery("(max-width:900px)");

  const faqs = [
    {
      q: "Where can I sign up to bid for your auctions?",
      a: "Please click on the link below to find a step-by-step guide on how to register and place bids for our upcoming lots.",
      link: "/how-to-buy",
      label: "Bidding Guide",
    },
    {
      q: "How can I purchase works outside of an auction?",
      a: "Private sales are available through our galleries. You may visit our locations or browse our private collections online.",
      link: publicUrls.privateSales("PAINTING"),
      label: "View Collections",
    },
    {
      q: "I am interested in consigning a piece. Can you assist?",
      a: "Certainly. Leon Gallery is always looking for exceptional works of art, jewelry, and watches. Follow the link to start your valuation process.",
      link: "/how-to-sell",
      label: "Consign with Us",
    },
    {
      q: "Where can I find the schedule for upcoming exhibitions and events?",
      a: "Our exhibition and auction calendars are updated regularly. You may also follow our social media channels for real-time announcements.",
      link: publicUrls.exhibitions(),
      label: "Event Calendar",
    },
    {
      q: "Where are your galleries located?",
      a: "We maintain multiple locations within Makati City to better serve our collectors. Detailed addresses and maps are available on our home page.",
      link: publicUrls.contact(),
      label: "Our Locations",
    },
    {
      q: "Is parking available at your locations?",
      a: "Eurovilla: Parking is available on Rufino and Rodriguez Streets. La Fuerza: Dedicated parking is available in front of the gallery. Corinthian Plaza: Limited parking; we recommend Greenbelt 1 parking for your convenience.",
      link: null,
    },
    {
      q: "Do you handle items beyond fine art?",
      a: "Yes. Our expertise extends to fine jewelry, timepieces, and objects d'art. For inquiries, please email us with clear images and any provenance information.",
      link: "mailto:info@leon-gallery.com",
      label: "Contact Specialist",
    },
  ];

  return (
    <Box sx={{ bgcolor: "#fff", minHeight: "100vh", color: "#111" }}>
      <Helmet>
        <title>Frequently Asked Questions | Leon Gallery</title>
      </Helmet>

      {/* HERO */}
      <Box
        sx={{
          bgcolor: "#111",
          color: "#fff",
          textAlign: "center",
          px: 3,
          py: isMobile ? 6 : 9,
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            letterSpacing: 4,
            textTransform: "uppercase",
            color: "#aaa",
            mb: 2,
          }}
        >
          Client Service
        </Typography>

        <Typography
          sx={{
            fontSize: isMobile ? 38 : 58,
            fontWeight: 400,
            lineHeight: 1,
            mb: 3,
          }}
        >
          Frequently Asked Questions
        </Typography>

        <Box
          sx={{
            width: 45,
            height: 1,
            bgcolor: "#fff",
            mx: "auto",
            mb: 3,
            opacity: 0.7,
          }}
        />

        <Typography
          sx={{
            color: "#ccc",
            fontSize: 13,
            letterSpacing: 1.5,
          }}
        >
          Information for collectors, bidders and consignors
        </Typography>
      </Box>

      <Container maxWidth="md" sx={{ py: isMobile ? 6 : 10 }}>
        {/* INTRO */}
        <Box sx={{ mb: 6 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 2,
            }}
          >
            <HelpOutlineOutlinedIcon sx={{ fontSize: 20 }} />

            <Typography
              sx={{
                fontSize: 11,
                letterSpacing: 3,
                textTransform: "uppercase",
                color: "#888",
              }}
            >
              Need assistance?
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: isMobile ? 28 : 38,
              fontWeight: 400,
              lineHeight: 1.2,
              mb: 2,
            }}
          >
            Answers to common questions.
          </Typography>

          <Typography
            sx={{
              color: "#666",
              fontSize: 14,
              lineHeight: 1.8,
              maxWidth: 680,
            }}
          >
            Find information about bidding, private sales, consignments,
            exhibitions, gallery locations and other services offered by León
            Gallery.
          </Typography>
        </Box>

        <Divider sx={{ mb: 1 }} />

        {/* FAQ LIST */}
        <Stack spacing={0}>
          {faqs.map((item, index) => (
            <Accordion
              key={item.q}
              elevation={0}
              disableGutters
              sx={{
                bgcolor: "transparent",
                borderBottom: "1px solid #ddd",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={{
                      fontSize: 20,
                      color: "#111",
                    }}
                  />
                }
                sx={{
                  px: 0,
                  py: isMobile ? 2 : 2.5,
                  minHeight: "auto",
                  "&.Mui-expanded": {
                    minHeight: "auto",
                  },
                  "& .MuiAccordionSummary-content": {
                    my: 0,
                    mr: 2,
                    alignItems: "flex-start",
                  },
                }}
              >
                <Typography
                  sx={{
                    minWidth: 38,
                    color: "#999",
                    fontSize: 10,
                    letterSpacing: 1,
                    pt: 0.5,
                    flexShrink: 0,
                  }}
                >
                  {String(index + 1).padStart(2, "0")}
                </Typography>

                <Typography
                  sx={{
                    fontSize: isMobile ? 15 : 17,
                    fontWeight: 500,
                    lineHeight: 1.5,
                  }}
                >
                  {item.q}
                </Typography>
              </AccordionSummary>

              <AccordionDetails
                sx={{
                  px: 0,
                  pb: 4,
                  pl: isMobile ? 0 : 4.75,
                }}
              >
                <Typography
                  sx={{
                    color: "#666",
                    fontSize: 14,
                    lineHeight: 1.9,
                    maxWidth: 700,
                    mb: item.link ? 3 : 0,
                  }}
                >
                  {item.a}
                </Typography>

                {item.link && (
                  <Box
                    component="a"
                    href={item.link}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      color: "#111",
                      textDecoration: "none",
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: 1.5,
                      textTransform: "uppercase",
                      borderBottom: "1px solid #111",
                      pb: 0.7,
                      transition: "all .2s ease",
                      "&:hover": {
                        color: "#777",
                        borderColor: "#777",
                        gap: 1.5,
                      },
                    }}
                  >
                    {item.label || "Read More"}
                    <ArrowForwardIcon sx={{ fontSize: 15 }} />
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>

        {/* CONTACT CTA */}
        <Box
          sx={{
            mt: isMobile ? 8 : 10,
            bgcolor: "#f6f6f6",
            textAlign: "center",
            px: isMobile ? 3 : 6,
            py: isMobile ? 5 : 7,
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              letterSpacing: 3,
              textTransform: "uppercase",
              color: "#888",
              mb: 2,
            }}
          >
            Still have questions?
          </Typography>

          <Typography
            sx={{
              fontSize: isMobile ? 27 : 36,
              fontWeight: 400,
              mb: 2,
            }}
          >
            Speak with our specialists.
          </Typography>

          <Typography
            sx={{
              color: "#666",
              fontSize: 13,
              lineHeight: 1.8,
              maxWidth: 560,
              mx: "auto",
              mb: 3.5,
            }}
          >
            Our support team is available Monday through Saturday, 9 AM – 6
            PM.
          </Typography>

          <Box
            component="a"
            href={publicUrls.contact()}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              color: "#fff",
              bgcolor: "#111",
              px: 3.5,
              py: 1.5,
              textDecoration: "none",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 1.5,
              textTransform: "uppercase",
              transition: "background .2s ease",
              "&:hover": {
                bgcolor: "#333",
              },
            }}
          >
            Contact Specialist
            <ArrowForwardIcon sx={{ fontSize: 16 }} />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Faq;
